/**
 * CambiarAgenciaDialog — corregir la agencia vendedora de una reserva.
 *
 * Pasa seguido: dos vendedores escriben el mismo día y la reserva queda a
 * nombre de la agencia equivocada. El nombre no vive solo en la reserva —queda
 * grabado en los vouchers, en las notas de los pagos y en el corte de
 * liquidación—, así que cambiarlo a mano en un solo sitio deja a una agencia
 * con la comisión de una venta que no hizo.
 *
 * Lo que NO cambia: el PVP, la comisión y los saldos. Todas las agencias de
 * Entretur manejan la misma comisión por paquete, así que la venta es idéntica
 * y solo cambió quién la hizo. Si las comisiones configuradas no coinciden,
 * eso es un error de configuración y el servidor frena: el diálogo lo explica
 * y deja seguir solo a sabiendas.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, TextField, Alert, Stack, Autocomplete, CircularProgress,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

export default function CambiarAgenciaDialog({
  open, onClose, reserva, onHecho, onToast,
}) {
  const [cargando, setCargando] = useState(false);
  const [agencias, setAgencias] = useState([]);
  const [comisionActual, setComisionActual] = useState(null);
  const [elegida, setElegida] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  // La comisión configurada no coincide. El servidor lo frena una vez y acá
  // se ofrece seguir dejando la comisión de la reserva como está.
  const [avisoComision, setAvisoComision] = useState(null);

  useEffect(() => {
    if (!open || !reserva?.id) return;
    setElegida(null); setMotivo(''); setError(null); setAvisoComision(null);
    setCargando(true);
    fetch(`${API_BASE}/turismo/correcciones/reservas/${reserva.id}/agencias-compatibles`,
          { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        if (d.detail) throw new Error(d.detail?.message || d.detail);
        // La actual no se ofrece: cambiarla por sí misma no es una corrección.
        setAgencias((d.agencias || []).filter(a => !a.es_la_actual));
        setComisionActual(d.comision_actual || null);
      })
      .catch(e => setError(e.message || 'No se pudieron cargar las agencias'))
      .finally(() => setCargando(false));
  }, [open, reserva?.id]);

  const nombreActual = reserva?.agencia_nombre
    || reserva?.agencia?.nombre || '—';

  const guardar = async (confirmando = false) => {
    setGuardando(true); setError(null);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/correcciones/reservas/${reserva.id}/cambiar-agencia`, {
          method: 'POST', headers: getAuthHeaders(),
          body: JSON.stringify({
            agencia_id: elegida.id,
            motivo: motivo.trim(),
            confirmar_comision_distinta: confirmando,
          }),
        });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        const det = d.detail;
        if (det?.code === 'COMISION_DISTINTA') {
          setAvisoComision(det.message); setGuardando(false); return;
        }
        throw new Error(det?.message || det || `Error ${r.status}`);
      }
      onToast?.success?.(
        `Agencia corregida: «${d.agencia_antes}» → «${d.agencia_nueva}».`);
      onHecho?.();
      onClose?.();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar la agencia');
    }
    setGuardando(false);
  };

  const puedeGuardar = !!elegida && motivo.trim().length >= 4 && !guardando && !cargando;

  const textoComision = (c) => c
    ? `${formatPrecio(c.adulto, reserva?.moneda)} adulto / ${formatPrecio(c.nino, reserva?.moneda)} niño`
    : 'sin comisión configurada para este paquete';

  return (
    <Dialog open={!!open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 17 }}>
        Cambiar la agencia vendedora
        <Typography sx={{ fontSize: 12.5, color: '#64748b', fontWeight: 500 }}>
          {reserva?.codigo_reserva} · {reserva?.nombre_cliente}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{error}</Alert>}

        {/* Comisiones distintas: no es un dato mal escrito, es configuración
            que alguien tiene que mirar. Se puede seguir, a sabiendas. */}
        {avisoComision && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}
                 action={
                   <Button size="small" disabled={guardando}
                           sx={{ textTransform: 'none', fontWeight: 700 }}
                           onClick={() => { setAvisoComision(null); guardar(true); }}>
                     Hacerlo igual
                   </Button>
                 }>
            {avisoComision}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {/* De quién a quién: lo que de verdad se está decidiendo */}
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: '#f8fafc',
                     border: '1px solid #e2e8f0', display: 'flex',
                     alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box>
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', textTransform: 'uppercase',
                                letterSpacing: 0.3, fontWeight: 700 }}>
                Figura hoy
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#64748b',
                                textDecoration: elegida ? 'line-through' : 'none' }}>
                {nombreActual}
              </Typography>
            </Box>
            {elegida && (
              <>
                <ArrowForwardIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                <Box>
                  <Typography sx={{ fontSize: 10.5, color: '#94a3b8', textTransform: 'uppercase',
                                    letterSpacing: 0.3, fontWeight: 700 }}>
                    Vendió en realidad
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                    {elegida.nombre}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          <Autocomplete
            options={agencias}
            loading={cargando}
            value={elegida}
            onChange={(e, v) => { setElegida(v); setAvisoComision(null); }}
            getOptionLabel={(o) => o?.nombre || ''}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            // Sin acuerdo para el paquete el servidor rechaza: no se ofrece.
            getOptionDisabled={(o) => !o.comision}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{o.nombre}</Typography>
                  <Typography sx={{ fontSize: 11,
                                    color: o.compatible ? '#64748b' : '#b45309' }}>
                    {textoComision(o.comision)}
                    {o.comision && !o.compatible ? ' · distinta a la actual' : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Agencia que hizo la venta"
                         placeholder="Escribe para buscar"
                         helperText={comisionActual
                           ? `La actual tiene ${textoComision(comisionActual)}`
                           : 'Este paquete no tiene comisión configurada'}
                         InputProps={{
                           ...params.InputProps,
                           endAdornment: (
                             <>
                               {cargando ? <CircularProgress size={16} /> : null}
                               {params.InputProps.endAdornment}
                             </>
                           ),
                         }} />
            )}
          />

          {/* Elegida una con comisión distinta: se avisa antes de intentarlo,
              no después del rechazo del servidor. */}
          {elegida && !elegida.compatible && elegida.comision && (
            <Alert severity="warning" sx={{ fontSize: 11.5 }}>
              «{elegida.nombre}» tiene {textoComision(elegida.comision)} y la actual
              tiene {textoComision(comisionActual)}. Todas las agencias deberían manejar
              la misma comisión: revisa la configuración antes de mover la reserva.
            </Alert>
          )}

          <TextField label="Por qué se cambia" size="small" multiline minRows={2}
                     value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder="La venta la hizo CTG Paradise, no Donas Travels"
                     helperText="Queda en la traza de auditoría" />

          <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#f0fdf4',
                     border: '1px solid #bbf7d0' }}>
            <Typography sx={{ fontSize: 11.5, color: '#166534', fontWeight: 700, mb: 0.25 }}>
              No cambia nada de plata
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#15803d' }}>
              El PVP, la comisión ({formatPrecio(reserva?.comision_valor, reserva?.moneda)}),
              los saldos y los cupos quedan igual: la venta es la misma, cambió quién la hizo.
              Se actualizan la reserva, los vouchers, las notas de los pagos y el corte de
              liquidación.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" startIcon={<StorefrontIcon />}
                disabled={!puedeGuardar} onClick={() => guardar(false)}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
          {guardando ? 'Cambiando…' : 'Cambiar la agencia'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
